<script setup>
import { getInboxLabel } from 'dashboard/helper/inboxLabel';
/* eslint-disable no-use-before-define */
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import TalkviaPortfolioManagementAPI from 'dashboard/api/talkviaPortfolioManagement';
import Select from 'dashboard/components-next/select/Select.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';

const route = useRoute();
const router = useRouter();
const dashboard = ref(null);
const isLoading = ref(false);
const selectedInbox = ref('');
const selectedAgent = ref('');
const dateRange = ref([new Date(), new Date()]);
const rangeType = ref(DATE_RANGE_TYPES.TODAY);

const data = computed(() => dashboard.value || {});
const filters = computed(() => data.value.filters || {});
const summary = computed(() => data.value.summary || {});
const agents = computed(() => data.value.agents || []);
const inboxes = computed(() => data.value.inboxes || []);
const alerts = computed(() => data.value.portfolio_alerts || []);
const inboxOptions = computed(() => [
  { value: '', label: 'Todas as caixas' },
  ...(filters.value.inboxes || []).map(inbox => ({
    value: inbox.id,
    label: getInboxLabel(inbox),
  })),
]);
const agentOptions = computed(() => [
  { value: '', label: 'Todos os agentes' },
  ...(filters.value.agents || []).map(agent => ({
    value: agent.id,
    label: agent.name,
  })),
]);

const summaryCards = computed(() => [
  {
    label: 'Clientes na carteira',
    value: formatNumber(summary.value.customers),
    icon: 'i-lucide-contact-round',
    tone: 'slate',
  },
  {
    label: 'Clientes ativos',
    value: formatNumber(summary.value.active_customers),
    icon: 'i-lucide-user-round-check',
    tone: 'brand',
  },
  {
    label: 'Atendidas no período',
    value: formatNumber(summary.value.handled_conversations),
    icon: 'i-lucide-messages-square',
    tone: 'blue',
  },
  {
    label: 'Aguardando resposta',
    value: formatNumber(summary.value.waiting_customers),
    icon: 'i-lucide-message-circle-warning',
    tone: 'amber',
  },
  {
    label: 'Atrasadas +30min',
    value: formatNumber(summary.value.overdue_customers),
    icon: 'i-lucide-timer-off',
    tone: 'ruby',
  },
  {
    label: 'Sem responsável',
    value: formatNumber(summary.value.unassigned_conversations),
    icon: 'i-lucide-user-round-x',
    tone: 'ruby',
  },
  {
    label: 'Sem movimentação 48h',
    value: formatNumber(summary.value.dormant_customers),
    icon: 'i-lucide-moon-star',
    tone: 'amber',
  },
  {
    label: 'Conversão da carteira',
    value: formatPercent(summary.value.conversion),
    icon: 'i-lucide-chart-no-axes-combined',
    tone: 'dark',
  },
]);

const loadDashboard = async () => {
  const [startDate, endDate] = dateRange.value;
  if (!startDate || !endDate) return;

  isLoading.value = true;
  try {
    const { data: response } = await TalkviaPortfolioManagementAPI.get({
      period: rangeType.value,
      date_start: formatDateInput(startDate),
      date_end: formatDateInput(endDate),
      ...(selectedInbox.value ? { inbox_id: selectedInbox.value } : {}),
      ...(selectedAgent.value ? { agent_id: selectedAgent.value } : {}),
    });
    dashboard.value = response;
  } catch {
    useAlert('Não foi possível carregar a gestão de carteira.');
  } finally {
    isLoading.value = false;
  }
};

const openConversation = item => {
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: route.params.accountId,
      conversation_id: item.conversation_display_id,
    },
  });
};

const onDateRangeChanged = ([startDate, endDate, selectedRange]) => {
  if (!startDate || !endDate) return;

  dateRange.value = [startDate, endDate];
  rangeType.value = selectedRange;
  loadDashboard();
};

function formatNumber(value) {
  return new Intl.NumberFormat('pt-BR').format(Number(value || 0));
}

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function formatPercent(value) {
  return `${Number(value || 0).toLocaleString('pt-BR', {
    maximumFractionDigits: 1,
  })}%`;
}

function formatDuration(value) {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds < 0) return '--';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}min`;
  if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.round((seconds % 3600) / 60);
    return `${hours}h ${minutes}min`;
  }
  const days = Math.floor(seconds / 86400);
  const hours = Math.round((seconds % 86400) / 3600);
  return `${days}d ${hours}h`;
}

function formatDateTime(value) {
  if (!value) return '--';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function formatDateInput(value) {
  const timezoneOffset = value.getTimezoneOffset() * 60 * 1000;
  return new Date(value.getTime() - timezoneOffset).toISOString().slice(0, 10);
}

function toneClass(tone) {
  return {
    slate: 'bg-n-slate-3 text-n-slate-11',
    brand: 'bg-n-brand/10 text-n-brand',
    blue: 'bg-n-blue-3 text-n-blue-11',
    amber: 'bg-n-amber-3 text-n-amber-11',
    ruby: 'bg-n-ruby-3 text-n-ruby-11',
    dark: 'bg-n-brand text-white',
  }[tone];
}

watch([selectedInbox, selectedAgent], loadDashboard);
onMounted(loadDashboard);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <main
    class="portfolio-dashboard flex h-full min-h-0 flex-col overflow-y-auto bg-n-background"
  >
    <header class="portfolio-dashboard__hero shrink-0 px-5 py-7 lg:px-8">
      <div
        class="mx-auto flex max-w-[100rem] flex-col gap-6 2xl:flex-row 2xl:items-end 2xl:justify-between"
      >
        <div class="flex min-w-0 items-start gap-4">
          <span
            class="grid size-14 shrink-0 place-items-center rounded-2xl bg-n-brand text-white shadow-lg shadow-n-brand/20"
          >
            <span class="i-lucide-users-round size-7" />
          </span>
          <div>
            <div
              class="mb-1 text-xs font-semibold uppercase tracking-[0.2em] text-[#8ec5ff]"
            >
              Operação · Gestão
            </div>
            <h1
              class="mb-1 text-3xl font-semibold tracking-[-0.04em] text-white"
            >
              Gestão de carteira
            </h1>
            <p class="mb-0 max-w-3xl text-sm leading-6 text-white/60">
              Clientes, capacidade de atendimento, tempos médios, produtividade
              e prioridades por caixa de entrada e agente.
            </p>
          </div>
        </div>

        <div
          class="portfolio-dashboard__filters grid w-full min-w-0 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:w-auto 2xl:grid-cols-[22rem_16rem_16rem]"
        >
          <label
            class="portfolio-dashboard__date-filter flex min-w-0 flex-col gap-1.5"
          >
            <span class="text-xs font-medium text-white/60">Período</span>
            <WootDatePicker
              v-model:date-range="dateRange"
              v-model:range-type="rangeType"
              @date-range-changed="onDateRangeChanged"
            />
          </label>
          <label class="flex min-w-0 flex-col gap-1.5">
            <span class="text-xs font-medium text-white/60">
              Caixa de entrada
            </span>
            <Select
              v-model="selectedInbox"
              :options="inboxOptions"
              class="portfolio-dashboard__filter-select w-full [&>select]:!h-10 [&>select]:w-full [&>select]:!rounded-xl [&>select]:!border-white/20 [&>select]:!bg-white/10 [&>select]:!px-3 [&>select]:!py-2 [&>select]:!text-white [&>select]:hover:!bg-white/15"
            />
          </label>
          <label class="flex min-w-0 flex-col gap-1.5">
            <span class="text-xs font-medium text-white/60">Agente</span>
            <Select
              v-model="selectedAgent"
              :options="agentOptions"
              class="portfolio-dashboard__filter-select w-full [&>select]:!h-10 [&>select]:w-full [&>select]:!rounded-xl [&>select]:!border-white/20 [&>select]:!bg-white/10 [&>select]:!px-3 [&>select]:!py-2 [&>select]:!text-white [&>select]:hover:!bg-white/15"
            />
          </label>
        </div>
      </div>
    </header>

    <section v-if="isLoading" class="grid flex-1 place-items-center p-10">
      <div class="flex items-center gap-2 text-sm text-n-slate-11">
        <span class="i-lucide-loader-2 size-5 animate-spin" />
        Carregando gestão de carteira...
      </div>
    </section>

    <section
      v-else
      class="mx-auto flex w-full max-w-[100rem] flex-col gap-6 p-5 lg:p-8"
    >
      <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-8">
        <article
          v-for="card in summaryCards"
          :key="card.label"
          class="portfolio-dashboard__panel rounded-2xl border border-n-weak bg-n-solid-2 p-4 transition hover:-translate-y-0.5 hover:border-n-strong"
        >
          <span
            class="mb-4 grid size-9 place-items-center rounded-xl"
            :class="toneClass(card.tone)"
          >
            <span class="size-5" :class="card.icon" />
          </span>
          <span class="block text-xs text-n-slate-10">{{ card.label }}</span>
          <strong class="mt-1 block text-xl text-n-slate-12">
            {{ card.value }}
          </strong>
        </article>
      </div>

      <section
        v-if="alerts.length"
        class="portfolio-dashboard__panel overflow-hidden rounded-[1.5rem] border border-n-amber-5 bg-n-solid-2"
      >
        <div
          class="flex items-start justify-between gap-4 border-b border-n-amber-5 bg-n-amber-2 px-5 py-4"
        >
          <div>
            <h2 class="mb-1 text-base font-semibold text-n-slate-12">
              Prioridades da carteira
            </h2>
            <p class="mb-0 text-sm text-n-slate-11">
              Clientes esperando resposta ou sem movimentação há mais de 48h.
            </p>
          </div>
          <span
            class="rounded-full bg-n-amber-4 px-3 py-1 text-xs font-semibold text-n-amber-12"
          >
            {{ alerts.length }} prioridades
          </span>
        </div>
        <div class="overflow-x-auto px-5 pb-2">
          <table class="w-full min-w-[60rem] text-left text-sm">
            <thead class="text-xs uppercase text-n-slate-10">
              <tr class="border-b border-n-weak">
                <th class="py-2 font-medium">Cliente</th>
                <th class="py-2 font-medium">Caixa</th>
                <th class="py-2 font-medium">Responsável</th>
                <th class="py-2 font-medium">Motivo</th>
                <th class="py-2 font-medium">Última atividade</th>
                <th class="py-2 font-medium">Proposta</th>
                <th class="py-2 text-right font-medium">Ação</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in alerts"
                :key="item.conversation_id"
                class="border-b border-n-weak transition hover:bg-n-alpha-1 last:border-0"
              >
                <td class="py-3 font-medium text-n-slate-12">
                  {{ item.customer }}
                </td>
                <td class="py-3 text-n-slate-11">{{ item.inbox }}</td>
                <td class="py-3 text-n-slate-11">{{ item.agent }}</td>
                <td class="py-3 text-n-slate-11">{{ item.reason }}</td>
                <td class="py-3 text-n-slate-10">
                  {{ formatDateTime(item.last_activity_at) }}
                </td>
                <td class="py-3 text-n-slate-11">
                  {{ item.proposal_status || 'Sem proposta' }}
                </td>
                <td class="py-3 text-right">
                  <button
                    type="button"
                    class="rounded-lg border border-n-amber-6 bg-n-amber-2 px-3 py-1.5 text-xs font-semibold text-n-amber-11 hover:bg-n-amber-3"
                    @click="openConversation(item)"
                  >
                    Abrir #{{ item.conversation_display_id }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section
        class="portfolio-dashboard__panel rounded-[1.5rem] border border-n-weak bg-n-solid-2 p-5"
      >
        <div class="mb-4">
          <h2 class="mb-0 text-base font-semibold text-n-slate-12">
            Visão geral de agentes
          </h2>
          <p class="mb-0 text-sm text-n-slate-11">
            Atendidas representa quem respondeu no período; carteira representa
            o responsável atual.
          </p>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full min-w-[75rem] text-left text-sm">
            <thead class="text-xs uppercase text-n-slate-10">
              <tr class="border-b border-n-weak">
                <th class="py-2 font-medium">Agente</th>
                <th class="py-2 font-medium">Atendidas</th>
                <th class="py-2 font-medium">Carteira</th>
                <th class="py-2 font-medium">Ativas</th>
                <th class="py-2 font-medium">Aguardando</th>
                <th class="py-2 font-medium">1ª resposta</th>
                <th class="py-2 font-medium">Resposta média</th>
                <th class="py-2 font-medium">Resolução</th>
                <th class="py-2 font-medium">Propostas</th>
                <th class="py-2 font-medium">Conversão</th>
                <th class="py-2 text-right font-medium">Produção</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="agent in agents"
                :key="agent.id"
                class="border-b border-n-weak last:border-0"
              >
                <td class="py-3 font-medium text-n-slate-12">
                  {{ agent.name }}
                </td>
                <td class="py-3 font-semibold text-n-slate-12">
                  {{ formatNumber(agent.handled_conversations) }}
                </td>
                <td class="py-3 text-n-slate-11">
                  {{ formatNumber(agent.portfolio_customers) }}
                </td>
                <td class="py-3 text-n-slate-11">
                  {{ formatNumber(agent.active_conversations) }}
                </td>
                <td class="py-3 text-n-slate-11">
                  {{ formatNumber(agent.waiting_customers) }}
                  <small v-if="agent.overdue_customers" class="text-n-ruby-11">
                    ({{ agent.overdue_customers }} atrasadas)
                  </small>
                </td>
                <td class="py-3 text-n-slate-11">
                  {{ formatDuration(agent.avg_first_response_time) }}
                </td>
                <td class="py-3 text-n-slate-11">
                  {{ formatDuration(agent.avg_reply_time) }}
                </td>
                <td class="py-3 text-n-slate-11">
                  {{ formatDuration(agent.avg_resolution_time) }}
                </td>
                <td class="py-3 text-n-slate-11">
                  {{ agent.paid_proposals }}/{{ agent.proposals }}
                </td>
                <td class="py-3 font-medium text-n-slate-12">
                  {{ formatPercent(agent.conversion) }}
                </td>
                <td class="py-3 text-right font-semibold text-n-slate-12">
                  {{ formatCurrency(agent.production) }}
                </td>
              </tr>
              <tr v-if="!agents.length">
                <td class="py-8 text-center text-n-slate-10" colspan="11">
                  Nenhum agente encontrado para os filtros selecionados.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section
        class="portfolio-dashboard__panel rounded-[1.5rem] border border-n-weak bg-n-solid-2 p-5"
      >
        <div class="mb-4">
          <h2 class="mb-0 text-base font-semibold text-n-slate-12">
            Gestão por caixa de entrada
          </h2>
          <p class="mb-0 text-sm text-n-slate-11">
            Capacidade, clientes, SLA e conversão comercial por operação.
          </p>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full min-w-[80rem] text-left text-sm">
            <thead class="text-xs uppercase text-n-slate-10">
              <tr class="border-b border-n-weak">
                <th class="py-2 font-medium">Caixa</th>
                <th class="py-2 font-medium">Atendidas</th>
                <th class="py-2 font-medium">Carteira</th>
                <th class="py-2 font-medium">Ativas</th>
                <th class="py-2 font-medium">Sem responsável</th>
                <th class="py-2 font-medium">Aguardando</th>
                <th class="py-2 font-medium">1ª resposta</th>
                <th class="py-2 font-medium">Resposta média</th>
                <th class="py-2 font-medium">Resolução</th>
                <th class="py-2 font-medium">Propostas</th>
                <th class="py-2 font-medium">Conversão</th>
                <th class="py-2 text-right font-medium">Produção</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="inbox in inboxes"
                :key="inbox.id"
                class="border-b border-n-weak last:border-0"
              >
                <td class="py-3 font-medium text-n-slate-12">
                  {{ inbox.name }}
                </td>
                <td class="py-3 font-semibold text-n-slate-12">
                  {{ formatNumber(inbox.handled_conversations) }}
                </td>
                <td class="py-3 text-n-slate-11">
                  {{ formatNumber(inbox.portfolio_customers) }}
                </td>
                <td class="py-3 text-n-slate-11">
                  {{ formatNumber(inbox.active_conversations) }}
                </td>
                <td class="py-3 text-n-ruby-11">
                  {{ formatNumber(inbox.unassigned_conversations) }}
                </td>
                <td class="py-3 text-n-slate-11">
                  {{ formatNumber(inbox.waiting_customers) }}
                  <small v-if="inbox.overdue_customers" class="text-n-ruby-11">
                    ({{ inbox.overdue_customers }} atrasadas)
                  </small>
                </td>
                <td class="py-3 text-n-slate-11">
                  {{ formatDuration(inbox.avg_first_response_time) }}
                </td>
                <td class="py-3 text-n-slate-11">
                  {{ formatDuration(inbox.avg_reply_time) }}
                </td>
                <td class="py-3 text-n-slate-11">
                  {{ formatDuration(inbox.avg_resolution_time) }}
                </td>
                <td class="py-3 text-n-slate-11">
                  {{ inbox.paid_proposals }}/{{ inbox.proposals }}
                </td>
                <td class="py-3 font-medium text-n-slate-12">
                  {{ formatPercent(inbox.conversion) }}
                </td>
                <td class="py-3 text-right font-semibold text-n-slate-12">
                  {{ formatCurrency(inbox.production) }}
                </td>
              </tr>
              <tr v-if="!inboxes.length">
                <td class="py-8 text-center text-n-slate-10" colspan="12">
                  Nenhuma caixa encontrada para os filtros selecionados.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </section>
  </main>
</template>

<style scoped>
.portfolio-dashboard__hero {
  border-bottom: 1px solid rgb(255 255 255 / 10%);
  background: radial-gradient(
      circle at 82% 8%,
      rgb(56 153 255 / 20%),
      transparent 32%
    ),
    linear-gradient(135deg, #071a33 0%, #0b3b70 52%, #0b2545 100%);
}

.portfolio-dashboard__panel {
  box-shadow:
    0 1px 2px rgb(15 23 42 / 4%),
    0 10px 30px rgb(15 23 42 / 4%);
}

.portfolio-dashboard__date-filter :deep(> div > div > button) {
  height: 2.5rem;
  max-width: 100%;
  border: 1px solid rgb(255 255 255 / 20%);
  border-radius: 0.75rem;
  background: rgb(255 255 255 / 10%);
  color: white;
}

.portfolio-dashboard__date-filter :deep(> div > div > button:hover) {
  background: rgb(255 255 255 / 15%);
}

.portfolio-dashboard__date-filter :deep(> div > div > button span),
.portfolio-dashboard__date-filter :deep(> div > div > button svg) {
  color: rgb(255 255 255 / 88%);
}

.portfolio-dashboard__filter-select :deep(option) {
  background: white;
  color: #172033;
}
</style>
